import { createServerFn } from "@tanstack/react-start";
import { getCookie, getRequestHeader, getRequestIP, setCookie } from "@tanstack/react-start/server";
import { eq, sql } from "drizzle-orm";
import { z } from "zod";
import { getDb, schema } from "@/db/client";
import { getLucia, type UserRole } from "@/auth/lucia";
import { portalOfRole } from "@/auth/portal";
import { requestPortal } from "@/auth/session";
import { verifyPassword } from "@/auth/password";
import { LoginThrottle, waitText } from "./login-throttle";

export interface CurrentUser {
  id: string;
  email: string;
  role: UserRole;
}

const throttle = new LoginThrottle();

/**
 * Адрес клиента. nginx перезаписывает X-Real-IP своим `$remote_addr`, его
 * клиент подделать не может — в отличие от X-Forwarded-For, куда nginx
 * только дописывает.
 */
function clientIp(): string | null {
  return getRequestHeader("x-real-ip")?.trim() || getRequestIP() || null;
}

/** Подрядчик в архиве не входит, даже с верным паролем. */
async function partnerMaySignIn(userId: string): Promise<boolean> {
  const rows = await getDb().execute<{ ok: boolean }>(
    sql`select public.partner_can_sign_in(${userId}::uuid) as ok`,
  );
  return Boolean(rows[0]?.ok);
}

export const loginFn = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) =>
    z
      .object({
        email: z.string().email().max(320),
        password: z.string().min(1).max(1024),
      })
      .parse(data),
  )
  .handler(
    async ({ data }): Promise<{ ok: true; user: CurrentUser } | { ok: false; error: string }> => {
      const email = data.email.trim().toLowerCase();
      const ip = clientIp();
      // Попытка занимается до проверки пароля — иначе залп проходит мимо
      // пустого счётчика. Удача её снимает (`succeed`).
      const wait = throttle.attempt(email, ip);
      if (wait > 0) {
        return { ok: false, error: `Слишком много попыток. Попробуйте через ${waitText(wait)}.` };
      }

      const db = getDb();
      const [row] = await db
        .select({
          id: schema.appUser.id,
          email: schema.appUser.email,
          role: schema.appUser.role,
          passwordHash: schema.appUser.passwordHash,
        })
        .from(schema.appUser)
        .where(eq(schema.appUser.email, email))
        .limit(1);

      if (!row) {
        // выравниваем тайминг: тратим argon2 verify против фиктивного hash, чтобы
        // ответ при неверном email шёл столько же, сколько при неверном пароле.
        await verifyPassword(
          "$argon2id$v=19$m=19456,t=2,p=1$ZmFrZWZha2VmYWtlZmFrZQ$ZmFrZWZha2VmYWtlZmFrZWZha2VmYWtlZmFrZWZha2U",
          data.password,
        );
        return { ok: false, error: "Неверный email или пароль" };
      }

      const ok = await verifyPassword(row.passwordHash, data.password);
      if (!ok) {
        return { ok: false, error: "Неверный email или пароль" };
      }

      // Пароль верный, но вход не тот. Говорим об этом прямо: человек ошибся
      // адресом, а не паролем, и подсказка ему нужнее вежливой немоты. Подбор
      // это не облегчает — до этой строки доходит только знающий пароль.
      const role = row.role as UserRole;
      const portal = requestPortal();
      if (portalOfRole(role) !== portal) {
        return {
          ok: false,
          error:
            portal === "partners"
              ? "Это вход для партнёров агентства. Сотрудникам — на основном адресе портала."
              : "Это вход для сотрудников. Партнёрам — в кабинет партнёра, по ссылке из приглашения.",
        };
      }
      if (role === "contractor" && !(await partnerMaySignIn(row.id))) {
        return { ok: false, error: "Доступ закрыт. Напишите тому, кто вас приглашал." };
      }

      throttle.succeed(email, ip);
      const lucia = getLucia();
      const session = await lucia.createSession(row.id, {});
      const cookie = lucia.createSessionCookie(session.id);
      setCookie(cookie.name, cookie.value, cookie.attributes);

      return {
        ok: true,
        user: { id: row.id, email: row.email, role },
      };
    },
  );

export const logoutFn = createServerFn({ method: "POST" }).handler(
  async (): Promise<{ ok: true }> => {
    const lucia = getLucia();
    const sessionId = getCookie(lucia.sessionCookieName);
    if (sessionId) {
      await lucia.invalidateSession(sessionId);
    }
    const blank = lucia.createBlankSessionCookie();
    setCookie(blank.name, blank.value, blank.attributes);
    return { ok: true };
  },
);

/**
 * Кто вошёл — с учётом портала: сессия чужого портала считается отсутствием
 * входа. В браузере так не бывает (кука привязана к хосту), только руками
 * подставленный Host; экран тогда просто ведёт на вход.
 */
export const getCurrentUserFn = createServerFn({ method: "GET" }).handler(
  async (): Promise<CurrentUser | null> => {
    const lucia = getLucia();
    const sessionId = getCookie(lucia.sessionCookieName);
    if (!sessionId) return null;

    const { session, user } = await lucia.validateSession(sessionId);
    if (!session) {
      const blank = lucia.createBlankSessionCookie();
      setCookie(blank.name, blank.value, blank.attributes);
      return null;
    }
    if (portalOfRole(user.role) !== requestPortal()) return null;
    if (session.fresh) {
      const refreshed = lucia.createSessionCookie(session.id);
      setCookie(refreshed.name, refreshed.value, refreshed.attributes);
    }
    return { id: user.id, email: user.email, role: user.role };
  },
);
